import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ShortUrlObjectType } from "@air/api/types";
import { Modal } from "@air/primitive-modal";
import { useEffect, useMemo } from "react";
import { NoPermissionToCreateShareLink } from "~/components/NoPermissionToCreateShareLink";
import { SharePrivateClipTabs } from "~/components/SharePrivateClipModal/SharePrivateClipTabs";
import { ReadonlySharePrivateLink } from "~/components/SharePrivateContentModal/ReadonlySharePrivateLink";
import { ShareModalHeader } from "~/components/SharePrivateContentModal/ShareModalHeader";
import { SharePrivateContentSpinner } from "~/components/SharePrivateContentModal/SharePrivateContentSpinner";
import { SHARE_PRIVATE_CONTENT_MODAL } from "~/constants/testIDs";
import { useShowTurnOnShareLinkNotifications } from "~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications";
import { useClipPermissions } from "~/hooks/useClipPermissions";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { useCurrentWorkspace } from "~/providers/CurrentWorkspaceProvider";
import { useGetExistingShortIdInformation } from "~/swr-hooks/shortUrl/useGetExistingShortIdInformation";
import { useGetShortIdForClip } from "~/swr-hooks/shortUrl/useGetShortIdForClip";
import { canSeeAssetVersions } from "~/utils/permissions/assetPermissions";
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from "~/utils/permissions/shortUrlPermissions";
export const SharePrivateClipModal = (param)=>{
    let { clip, onClose, trackLocation, boardContext } = param;
    const { currentWorkspace } = useCurrentWorkspace();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const { clipPermissions } = useClipPermissions({
        clipId: clip.id
    });
    const canEdit = canEditShortUrl(clipPermissions);
    const canCreate = canCreateShortUrl(clipPermissions);
    const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();
    const { data: shortIdInfo, isLoading: isInitialLoading } = useGetExistingShortIdInformation({
        id: clip.id,
        boardContext,
        type: ShortUrlObjectType.clip,
        canView: canViewShortUrl(clipPermissions)
    });
    const { getShortIdForClip } = useGetShortIdForClip();
    useEffect(()=>{
        const workspaceId = currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id;
        if (!workspaceId) {
            return;
        }
        if (!isInitialLoading && !shortIdInfo && canCreate && !getShortIdForClip.isPending) {
            getShortIdForClip.mutate({
                workspaceId,
                id: clip.id,
                boardContext
            }, {
                onSuccess: ()=>{
                    showTurnOnShareLinkNotifications({
                        onManageClick: onClose
                    });
                }
            });
        }
    }, [
        currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id,
        clip.id,
        canCreate,
        getShortIdForClip,
        isInitialLoading,
        shortIdInfo,
        boardContext,
        showTurnOnShareLinkNotifications,
        onClose
    ]);
    const ModalContent = useMemo(()=>{
        if (!clipPermissions) {
            return /*#__PURE__*/ _jsx(SharePrivateContentSpinner, {});
        } else if (!shortIdInfo) {
            if (canCreate) {
                return /*#__PURE__*/ _jsx(SharePrivateContentSpinner, {});
            } else {
                return /*#__PURE__*/ _jsx(NoPermissionToCreateShareLink, {});
            }
        } else {
            if (!canEdit) {
                return /*#__PURE__*/ _jsx(ReadonlySharePrivateLink, {
                    objectType: ShortUrlObjectType.clip,
                    shareId: shortIdInfo.id,
                    objectId: clip.id,
                    trackLocation: trackLocation
                });
            } else {
                return /*#__PURE__*/ _jsx(SharePrivateClipTabs, {
                    clip: clip,
                    trackLocation: trackLocation,
                    shortIdInfo: shortIdInfo,
                    boardContext: boardContext
                });
            }
        }
    }, [
        boardContext,
        canCreate,
        canEdit,
        clip,
        clipPermissions,
        shortIdInfo,
        trackLocation
    ]);
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    const modalTitle = "Share a link to ".concat(clip.assetVersionCount && !canSeeAssetVersions(shortIdInfo === null || shortIdInfo === void 0 ? void 0 : shortIdInfo.permissions) ? "V".concat(clip.assetVersionCount) : "asset");
    return /*#__PURE__*/ _jsxs(Modal, {
        "data-testid": SHARE_PRIVATE_CONTENT_MODAL,
        dangerouslyBypassFocusLock: true,
        isOpen: true,
        onDismiss: onClose,
        children: [
            /*#__PURE__*/ _jsx(ShareModalHeader, {
                onClose: onClose,
                title: modalTitle
            }),
            ModalContent
        ]
    });
};
